import type { MonospacePolicyEntitlementResource } from '#layers/access/public';
import type { GlobalCollectionId } from '#layers/data-model/public';
import * as z from 'zod/v4-mini';

/**
 * The entitlement descriptor is fetched from the engine and drives the authorization
 * hot path (`descriptorHasAction`/`isScoped` call `.find` on `actions`). A malformed or
 * partial payload must degrade to "not covered" — never throw inside a render/`isVisible`
 * gate — so the wire response is validated here and malformed resources are dropped rather
 * than trusted. A missing/non-array `resources` yields an empty descriptor.
 */

const actionSchema = z.object({
	name: z.string(),
	hasIdentifier: z.boolean(),
});

const resourceSchema = z.object({
	name: z.string(),
	actions: z.array(actionSchema),
	collectionId: z.optional(z.string()),
});

export function parseEntitlementResources(response: unknown): MonospacePolicyEntitlementResource[] {
	const resources = (response as { resources?: unknown } | null | undefined)?.resources;

	if (!Array.isArray(resources)) return [];

	const valid: MonospacePolicyEntitlementResource[] = [];

	for (const resource of resources) {
		const parsed = resourceSchema.safeParse(resource);
		if (!parsed.success) continue;

		valid.push({
			name: parsed.data.name,
			actions: parsed.data.actions,
			collectionId: parsed.data.collectionId as GlobalCollectionId | undefined,
		});
	}

	return valid;
}
